import React, { useEffect, useState } from "react";
import axios from "axios";
import "../../../assets/styles/AuctionPage.css";
import { Link, useLocation, useNavigate } from "react-router-dom";
import Navbar from "../../../components/AuctionFeatureComponents/Home/Navbar";
import { checkIfAdmin } from "../../../Utils/AuthUtils";
import { BACKEND_DOMAIN } from "../../../common/env";

const AuctionsPage = () => {
  const [auctions, setAuctions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeCategory, setActiveCategory] = useState("upcoming");
  const navigate = useNavigate();
  const location = useLocation();
  
  const token = localStorage.getItem("token");

  // Fetch auctions based on the selected category
  const fetchAuctions = async () => {
    try {
      setLoading(true);
      setError(null); // Clear any previous errors
      if (activeCategory === "my-auctions" && token) {
        const response = await axios.get(`${BACKEND_DOMAIN}/myAuctions`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setAuctions(response.data);
      } else {
        const response = await axios.get(`${BACKEND_DOMAIN}/all-auctions`);
        setAuctions(response.data.auctions || []);
      }
    } catch (error) {
      console.error("Error fetching auctions:", error);
      setError("Failed to load auctions. Please try again.");
      setAuctions([]); // Clear auctions on error
    } finally {
      setLoading(false);
    }
  };

  // Fetch auctions when component mounts or when location changes
  useEffect(() => {
    fetchAuctions();
  }, [activeCategory, token, location.key]); // Added location.key to force refresh on navigation

  // Always default to upcoming category when component mounts or navigation occurs
  useEffect(() => {
    setActiveCategory("upcoming");
  }, [location.key]);

  const handleDelete = async (auctionId) => {
    const confirmDelete = window.confirm('Are you sure you want to delete this auction?');
    if (!confirmDelete) return;

    try {
      await axios.delete(`${BACKEND_DOMAIN}/auctions/${auctionId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setAuctions((prev) => prev.filter((auction) => auction._id !== auctionId));
      alert('Auction deleted successfully.');
    } catch (err) {
      console.error(err);
      alert('Failed to delete auction.');
    }
  };

  const handleView = async (auctionId) => {
    try {
      // Check if user is admin for this specific auction
      const token = localStorage.getItem('token');
      if (!token) {
        // If no token, default to user view (allow unauthenticated access)
        navigate(`/live-auction/${auctionId}/user-live-bidding`);
        return;
      }
      
      const response = await axios.get(`${BACKEND_DOMAIN}/auctions/${auctionId}/is-admin`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      const isAdmin = response.data.isAdmin;
      
      // Route based on admin status
      if (isAdmin) {
        navigate(`/live-auction/${auctionId}/admin-live-bidding`);
      } else {
        navigate(`/live-auction/${auctionId}/user-live-bidding`);
      }
    } catch (error) {
      console.error('Error checking admin status:', error);
      // Default to user view if there's an error
      navigate(`/live-auction/${auctionId}/user-live-bidding`);
    }
  };

  // Categorize auctions
  const categorizeAuctions = () => {
    const currentDate = new Date();

    const upcoming = auctions.filter((auction) => new Date(auction.date) > currentDate);
    const ongoing = auctions.filter((auction) => new Date(auction.date).toDateString() === currentDate.toDateString());
    const past = auctions.filter((auction) => new Date(auction.date) < currentDate);

    return { upcoming, ongoing, past };
  };

  const { upcoming, ongoing, past } = categorizeAuctions();

  // Get current auctions based on active category
  const getCurrentAuctions = () => {
    if (activeCategory === "my-auctions") return auctions;
    if (activeCategory === "upcoming") return upcoming;
    if (activeCategory === "ongoing") return ongoing;
    if (activeCategory === "past") return past;
    return [];
  };

  const currentAuctions = getCurrentAuctions();

  const getStatusColor = (auction) => {
    const currentDate = new Date();
    const auctionDate = new Date(auction.date);
    
    if (auctionDate > currentDate) return 'upcoming';
    if (auctionDate.toDateString() === currentDate.toDateString()) return 'ongoing';
    return 'past';
  };

  return (
    <div className="auctions-page">
      <Navbar />
      
      {/* Full-width background container */}
      <div className="auctions-background">
        <div className="auctions-container">
          
          {/* Modern Tab Navigation */}
          <div className="tabs-container">
            <div className="tabs-wrapper">
              <button
                className={`tab-button ${activeCategory === "ongoing" ? "active" : ""}`}
                onClick={() => setActiveCategory("ongoing")}
              >
                <span className="tab-count">{ongoing.length}</span>
                <span className="tab-label">Ongoing</span>
              </button>
              
              <button
                className={`tab-button ${activeCategory === "upcoming" ? "active" : ""}`}
                onClick={() => setActiveCategory("upcoming")}
              >
                <span className="tab-count">{upcoming.length}</span>
                <span className="tab-label">Upcoming</span>
              </button>
              
              <button
                className={`tab-button ${activeCategory === "past" ? "active" : ""}`}
                onClick={() => setActiveCategory("past")}
              >
                <span className="tab-count">{past.length}</span>
                <span className="tab-label">Past</span>
              </button>
            </div>
          </div>

          {/* Content Area */}
          <div className="auctions-content">
            {loading ? (
              <div className="loading-container">
                <div className="loading-spinner"></div>
                <p>Loading auctions...</p>
              </div>
            ) : error ? (
              <div className="error-container">
                <p className="error-message">{error}</p>
                <button 
                  className="create-auction-btn"
                  onClick={() => navigate('/admin/create-auction')}
                >
                  No Auction Found.
                </button>
              </div>
            ) : (
              <div className="auction-grid">
                {currentAuctions.length > 0 ? (
                  currentAuctions.map((auction) => (
                    <div 
                      key={auction._id} 
                      className={`auction-card-modern ${getStatusColor(auction)}`}
                      onClick={() => handleView(auction._id)}
                    >
                      <div className="auction-content-wrapper">
                        {/* Auction Logo Section - Left */}
                        <div className="auction-logo-section">
                          {auction.auctionLogo ? (
                            <img 
                              src={auction.auctionLogo} 
                              alt={`${auction.title} logo`}
                              className="auction-logo-img"
                            />
                          ) : (
                            <div className="auction-logo-placeholder">
                              {auction.title.split(' ').map(word => word[0]).join('').toUpperCase().slice(0, 2)}
                            </div>
                          )}
                        </div>
                        
                        {/* Auction Details Section - Right */}
                        <div className="auction-details-section">
                          <div className="card-status-badge">
                            {getStatusColor(auction)}
                          </div>
                          <div className="card-content">
                            <h3 className="card-title">{auction.title}</h3>
                            <div className="card-details">
                              <div className="detail-item">
                                <span className="detail-label">Organizer:</span>
                                <span className="detail-value">{auction.organizer}</span>
                              </div>
                              <div className="detail-item">
                                <span className="detail-label">Date & Time:</span>
                                <span className="detail-value">{new Date(auction.date).toLocaleString()}</span>
                              </div>
                              <div className="detail-item">
                                <span className="detail-label">Location:</span>
                                <span className="detail-value">{auction.location}</span>
                              </div>
                              <div className="detail-item">
                                <span className="detail-label">Sport:</span>
                                <span className="detail-value">{auction.sport}</span>
                              </div>
                            </div>
                          </div>

                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="empty-state">
                    <div className="empty-icon">📅</div>
                    <h3>No {activeCategory.replace('-', ' ')} auctions</h3>
                    <p>There are currently no {activeCategory.replace('-', ' ')} auctions available.</p>
                    <p className="empty-description">
                      {activeCategory === "upcoming" 
                        ? "Check back later for upcoming auctions or create your own auction if you're an organizer."
                        : activeCategory === "ongoing"
                        ? "No auctions are currently running. Check the upcoming tab for future events."
                        : "No past auctions found. Completed auctions will appear here after they finish."}
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
          
        </div>
      </div>
    </div>
  );
};

export default AuctionsPage;